import { Box } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { getDataGridLocalization, MgPrisLocale } from '@retail/utils';
import { AddButton, NoTableRowsOverlay, StackedTextCell } from '@retail/components';
import React, { useMemo } from 'react';
import { ConditionsNew } from '@retail/calculus/types';
import { useAppTFunction } from '@retail/app/i18n';
import { LevelChip } from '../ConditionApproval';
import dayjs from 'dayjs';
import { dataGridNoHorizontalPaddingClass, dataGridWithCellBordersClass } from '@shared/styles';
import { useRowColorGrouping } from '@retail/hooks';
import { ConditionsTableActions } from '../ConditionsTableActions';
import { SalesPriceConditionValue } from './SalesPriceConditionValue';

const baseColumnProps: Partial<GridColDef<ConditionsNew>> = {
  sortable: false,
  width: 150,
  flex: 1,
  headerAlign: 'left',
  align: 'left',
};

interface Props {
  conditions: ConditionsNew[];
  language: string;
  loading: boolean;
  calculationLevel: 'assortment' | 'supplier';
  createCondition?: () => void;
  editCondition?: (condition: ConditionsNew) => void;
  deactivateCondition?: (conditionId?: number[]) => void;
  deleteCondition?: (conditionId?: number[]) => void;
}

export const SalesPriceConditionsTable: React.FC<Props> = ({
  conditions,
  loading,
  language,
  calculationLevel,
  createCondition,
  editCondition,
  deactivateCondition,
  deleteCondition,
}) => {
  const t = useAppTFunction();
  const { getRowColorForStripedTable } = useRowColorGrouping();

  const columns: GridColDef<ConditionsNew>[] = useMemo(() => {
    return [
      {
        ...baseColumnProps,
        field: 'level',
        headerName: t('markup.level'),
        flex: 0,
        width: 190,
        renderCell: ({ row }) => (
          <LevelChip
            bindingType={row.bindingType}
            active={!row.inDraft}
            bindingTargetItem={row.bindingTargetItem}
          />
        ),
      },
      {
        ...baseColumnProps,
        field: 'supplier',
        headerName: t('calculus.level.supplier'),
        width: 190,
        valueGetter: (_, row) => row.supplier?.supplierNumber,
        renderCell: ({ row }) => {
          return (
            <StackedTextCell
              topLine={row.supplier?.name}
              bottomLine={row.supplier?.supplierNumber}
            />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'name',
        headerName: t('markup.levelOrItemName'),
        width: 260,
        renderCell: ({ row }) => {
          return (
            <StackedTextCell topLine={row.bindingTargetName} bottomLine={row.bindingTargetId} />
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'priority',
        headerName: t('calculus.salesPriceConditions.priority'),
        width: 200,
        cellClassName: dataGridNoHorizontalPaddingClass,
        renderCell: ({ row }) => {
          return <SalesPriceConditionValue condition={row} />;
        },
      },
      {
        ...baseColumnProps,
        field: 'activeFromTo',
        headerAlign: 'center',
        align: 'center',
        width: 180,
        headerName: t('markup.activeFromTo'),
        renderCell: ({ row }) => {
          const activeFrom = dayjs(row.priceSourceCondition?.validFrom).format('ll');
          const activeTo =
            row.priceSourceCondition?.validTo &&
            dayjs(row.priceSourceCondition?.validTo).format('ll');
          return <StackedTextCell topLine={activeFrom} bottomLine={activeTo} centerAlign />;
        },
      },

      {
        ...baseColumnProps,
        field: 'actions',
        headerName: '',
        flex: 0,
        width: 96,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <ConditionsTableActions
            handleEdit={editCondition ? () => editCondition(row) : undefined}
            handleDeactivate={
              deactivateCondition && !row.inDraft
                ? () => deactivateCondition(row.types.map((type) => type.id))
                : undefined
            }
            handleDelete={
              deleteCondition && row.inDraft
                ? () => deleteCondition(row.types.map((type) => type.id))
                : undefined
            }
          />
        ),
      },
    ];
  }, [deactivateCondition, deleteCondition, editCondition, t]);

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0, width: '100%' }}>
        <DataGridPremium
          getRowId={(row) => row.types.map((type) => type.id).join(',')}
          getRowClassName={getRowColorForStripedTable}
          columns={columns}
          rows={conditions}
          getRowHeight={() => 'auto'}
          loading={loading}
          className={dataGridWithCellBordersClass}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          columnVisibilityModel={{ supplier: calculationLevel === 'assortment' }}
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          hideFooter
          sx={{ '& .MuiDataGrid-cell': { py: 2 } }}
          slots={{
            noRowsOverlay: () => (
              <NoTableRowsOverlay
                noRowsText={t('calculus.salesPriceConditions.noConditions')}
                addAction={
                  !!createCondition && (
                    <AddButton onClick={createCondition}>
                      {t('calculus.salesPriceConditions.create')}
                    </AddButton>
                  )
                }
              />
            ),
          }}
        />
      </Box>
    </Box>
  );
};
